// src/AdminPage.jsx
// ─────────────────────────────────────────────
// 관리자 페이지
// 탭 구성:
//   수강생 관리 — 등록 / 목록 / 기간 연장 / 비활성화
//   관리자 관리 — 관리자 추가 / 제거
//   설정        — 수강 기간 기본값 변경
// ─────────────────────────────────────────────

import React, { useEffect, useState, useCallback } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc,
  deleteDoc, getDoc, Timestamp, orderBy, query,
} from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import { resolveStudentStatus } from './AuthContext';

// ── 상수 ─────────────────────────────────────
const TABS = ['수강생 관리', '관리자 관리', '설정'];

const STATUS_LABEL = {
  active:         { label: '수강 중',     color: '#2C6E49', bg: '#EBF5EF' },
  'expiring-soon':{ label: '만료 임박',   color: '#854F0B', bg: '#FDF3E7' },
  expired:        { label: '만료',        color: '#B91C1C', bg: '#FEF2F2' },
  inactive:       { label: '비활성',      color: '#5C5C72', bg: '#F7F6F4' },
  'not-enrolled': { label: '미등록',      color: '#9898AA', bg: '#F7F6F4' },
};

// ── 메인 컴포넌트 ─────────────────────────────
export default function AdminPage() {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div style={s.page}>
      {/* 헤더 */}
      <header style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.headerTitle}>관리자 페이지</div>
          <div style={s.headerSub}>이러닝 교수설계 입문</div>
        </div>
        <div style={s.headerRight}>
          <span style={s.adminBadge}>관리자</span>
          <span style={s.adminEmail}>{user?.email}</span>
          <button style={s.signOutBtn} onClick={signOut}>로그아웃</button>
        </div>
      </header>

      {/* 탭 */}
      <div style={s.tabBar}>
        {TABS.map((tab, i) => (
          <button
            key={tab}
            style={{ ...s.tab, ...(activeTab === i ? s.tabActive : {}) }}
            onClick={() => setActiveTab(i)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* 탭 콘텐츠 */}
      <div style={s.content}>
        {activeTab === 0 && <StudentTab currentAdminEmail={user?.email} />}
        {activeTab === 1 && <AdminTab currentAdminEmail={user?.email} />}
        {activeTab === 2 && <SettingsTab />}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════
// 수강생 관리 탭
// ══════════════════════════════════════════════
function StudentTab({ currentAdminEmail }) {
  const [students,   setStudents]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [enrollDays, setEnrollDays] = useState(90);

  // 등록 폼 상태
  const [newEmail,   setNewEmail]   = useState('');
  const [newMemo,    setNewMemo]    = useState('');
  const [enrollMsg,  setEnrollMsg]  = useState(null); // { type, text }
  const [enrolling,  setEnrolling]  = useState(false);

  // 기간 연장 모달 상태
  const [extendTarget, setExtendTarget] = useState(null); // student doc
  const [extendDays,   setExtendDays]   = useState(30);
  const [extendMsg,    setExtendMsg]    = useState('');

  const loadStudents = useCallback(async (retryCount = 0) => {
    setLoading(true);
    try {
      const configSnap = await getDoc(doc(db, 'settings', 'config'));
      if (configSnap.exists()) setEnrollDays(configSnap.data().enrollmentDays ?? 90);

      const q = query(collection(db, 'students'), orderBy('enrolledAt', 'desc'));
      const snap = await getDocs(q);
      setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      // 권한 오류 시 최대 3회 재시도 (토큰 반영 지연 대응)
      if (err.code === 'permission-denied' && retryCount < 3) {
        setTimeout(() => loadStudents(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      console.error('loadStudents 오류:', err.code, err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  // 수강생 등록
  const handleEnroll = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setEnrolling(true);
    setEnrollMsg(null);
    try {
      const existing = await getDoc(doc(db, 'students', email));
      if (existing.exists()) {
        setEnrollMsg({ type: 'warn', text: `이미 등록된 수강생입니다. (${email})` });
        return;
      }
      const now       = Timestamp.now();
      const expiresAt = Timestamp.fromMillis(now.toMillis() + enrollDays * 86_400_000);
      await setDoc(doc(db, 'students', email), {
  email,
  displayName: '',
  enrolledAt: now,
  expiresAt,
  active: true,
  studentStatus: 'approved',
  memo: newMemo.trim(),
  addedBy: currentAdminEmail,
});
      setEnrollMsg({ type: 'ok', text: `등록 완료: ${email}` });
      setNewEmail('');
      setNewMemo('');
      await loadStudents();
    } catch (err) {
      setEnrollMsg({ type: 'err', text: '등록 중 오류가 발생했습니다.' });
      console.error(err);
    } finally {
      setEnrolling(false);
    }
  };

  // 활성/비활성 토글
  const toggleActive = async (student) => {
    await updateDoc(doc(db, 'students', student.email), {
      active: !student.active,
    });
    await loadStudents();
  };

  // 기간 연장 실행
  const handleExtend = async () => {
    if (!extendTarget) return;
    const currentExp = extendTarget.expiresAt?.toMillis() ?? Date.now();
    const newExp = Timestamp.fromMillis(currentExp + extendDays * 86_400_000);
    await updateDoc(doc(db, 'students', extendTarget.email), { expiresAt: newExp });
    setExtendMsg(`${extendDays}일 연장 완료`);
    setTimeout(() => { setExtendTarget(null); setExtendMsg(''); }, 1200);
    await loadStudents();
  };

  const fmt = (ts) => {
    if (!ts) return '-';
    const d = ts.toDate?.() ?? new Date(ts);
    return d.toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' });
  };

  const activeCount  = students.filter(s => resolveStudentStatus(s) === 'active').length;
  const expiringCount = students.filter(s => resolveStudentStatus(s) === 'expiring-soon').length;
  const expiredCount = students.filter(s =>
    resolveStudentStatus(s) === 'expired' || resolveStudentStatus(s) === 'inactive'
  ).length;

  return (
    <div>
      {/* 요약 카드 */}
      <div style={s.summaryRow}>
        <SummaryCard label="전체 수강생" value={students.length} />
        <SummaryCard label="수강 중"     value={activeCount}      color="#2C6E49" />
        <SummaryCard label="만료 임박"   value={expiringCount}    color="#854F0B" />
        <SummaryCard label="만료/비활성" value={expiredCount}     color="#B91C1C" />
      </div>

      {/* 수강생 등록 폼 */}
      <div style={s.section}>
        <div style={s.sectionTitle}>수강생 등록</div>
        <form onSubmit={handleEnroll} style={s.enrollForm}>
          <input
            type="email" required
            placeholder="수강생 Google 이메일"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            style={s.input}
          />
          <input
            type="text"
            placeholder="메모 (선택)"
            value={newMemo}
            onChange={e => setNewMemo(e.target.value)}
            style={{ ...s.input, maxWidth: '200px' }}
          />
          <button type="submit" style={s.btnPrimary} disabled={enrolling}>
            {enrolling ? '등록 중...' : `등록 (${enrollDays}일)`}
          </button>
        </form>
        {enrollMsg && (
          <div style={{
            ...s.msgBox,
            background: enrollMsg.type === 'ok' ? '#EBF5EF' : enrollMsg.type === 'warn' ? '#FDF3E7' : '#FEF2F2',
            color:      enrollMsg.type === 'ok' ? '#2C6E49' : enrollMsg.type === 'warn' ? '#854F0B' : '#B91C1C',
          }}>
            {enrollMsg.text}
          </div>
        )}
      </div>

      {/* 수강생 목록 */}
      <div style={s.section}>
        <div style={s.sectionTitle}>수강생 목록</div>
        {loading ? (
          <div style={s.loadingText}>불러오는 중...</div>
        ) : students.length === 0 ? (
          <div style={s.emptyText}>등록된 수강생이 없습니다.</div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {['이름', '이메일', '등록일', '만료일', '상태', '작업'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map(st => {
                  const status = resolveStudentStatus(st);
                  const sl     = STATUS_LABEL[status] || STATUS_LABEL['not-enrolled'];
                  return (
                    <tr key={st.id} style={{ background: status === 'expiring-soon' ? '#FFFBF0' : 'transparent' }}>
                      <td style={s.td}>{st.displayName || <span style={{ color: '#9898AA' }}>-</span>}</td>
                      <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px' }}>{st.email}</td>
                      <td style={s.td}>{fmt(st.enrolledAt)}</td>
                      <td style={s.td}>{fmt(st.expiresAt)}</td>
                      <td style={s.td}>
                        <span style={{ ...s.statusBadge, background: sl.bg, color: sl.color }}>
                          {sl.label}
                        </span>
                      </td>
                      <td style={s.td}>
                        <div style={s.actionRow}>
                          <button
                            style={s.btnMini}
                            onClick={() => { setExtendTarget(st); setExtendDays(30); }}
                          >
                            연장
                          </button>
                          <button
                            style={{
                              ...s.btnMini,
                              background: st.active ? '#FEF2F2' : '#EBF5EF',
                              color:      st.active ? '#B91C1C' : '#2C6E49',
                            }}
                            onClick={() => toggleActive(st)}
                          >
                            {st.active ? '비활성화' : '활성화'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 기간 연장 모달 */}
      {extendTarget && (
        <Modal onClose={() => setExtendTarget(null)}>
          <div style={s.modalTitle}>수강 기간 연장</div>
          <div style={s.modalDesc}>
            <strong>{extendTarget.email}</strong><br />
            현재 만료일: {fmt(extendTarget.expiresAt)}
          </div>
          <div style={s.modalRow}>
            <label style={s.modalLabel}>연장 일수</label>
            <input
              type="number" min="1" max="365"
              value={extendDays}
              onChange={e => setExtendDays(Number(e.target.value))}
              style={{ ...s.input, width: '100px' }}
            />
            <span style={{ fontSize: '13px', color: '#5C5C72' }}>일</span>
          </div>
          {extendMsg && <div style={{ ...s.msgBox, background: '#EBF5EF', color: '#2C6E49' }}>{extendMsg}</div>}
          <div style={s.modalBtns}>
            <button style={s.btnSecondary} onClick={() => setExtendTarget(null)}>취소</button>
            <button style={s.btnPrimary}   onClick={handleExtend}>연장 적용</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════
// 관리자 관리 탭
// ══════════════════════════════════════════════
function AdminTab({ currentAdminEmail }) {
  const [admins,  setAdmins]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [msg, setMsg] = useState(null);

  const loadAdmins = useCallback(async (retryCount = 0) => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'admins'));
      setAdmins(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      if (err.code === 'permission-denied' && retryCount < 3) {
        setTimeout(() => loadAdmins(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      console.error('loadAdmins 오류:', err.code, err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const email = newAdminEmail.trim().toLowerCase();
    if (!email) return;
    setMsg(null);
    try {
      const existing = await getDoc(doc(db, 'admins', email));
      if (existing.exists()) {
        setMsg({ type: 'warn', text: '이미 관리자로 등록된 계정입니다.' });
        return;
      }
      await setDoc(doc(db, 'admins', email), {
        email,
        addedAt: Timestamp.now(),
        addedBy: currentAdminEmail,
      });
      setMsg({ type: 'ok', text: `관리자 추가 완료: ${email}` });
      setNewAdminEmail('');
      await loadAdmins();
    } catch (err) {
      setMsg({ type: 'err', text: '오류가 발생했습니다.' });
    }
  };

  const handleRemove = async (email) => {
    if (email === currentAdminEmail) {
      alert('본인 계정은 삭제할 수 없습니다.');
      return;
    }
    if (!window.confirm(`${email} 관리자를 삭제하시겠습니까?`)) return;
    await deleteDoc(doc(db, 'admins', email));
    await loadAdmins();
  };

  return (
    <div>
      <div style={s.section}>
        <div style={s.sectionTitle}>관리자 추가</div>
        <form onSubmit={handleAdd} style={s.enrollForm}>
          <input
            type="email" required
            placeholder="추가할 관리자 Google 이메일"
            value={newAdminEmail}
            onChange={e => setNewAdminEmail(e.target.value)}
            style={s.input}
          />
          <button type="submit" style={s.btnPrimary}>관리자 추가</button>
        </form>
        {msg && (
          <div style={{
            ...s.msgBox,
            background: msg.type === 'ok' ? '#EBF5EF' : msg.type === 'warn' ? '#FDF3E7' : '#FEF2F2',
            color:      msg.type === 'ok' ? '#2C6E49' : msg.type === 'warn' ? '#854F0B' : '#B91C1C',
          }}>
            {msg.text}
          </div>
        )}
      </div>

      <div style={s.section}>
        <div style={s.sectionTitle}>관리자 목록</div>
        {loading ? (
          <div style={s.loadingText}>불러오는 중...</div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {['이메일', '등록일', '등록자', ''].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {admins.map(admin => (
                  <tr key={admin.id}>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px' }}>
                      {admin.email}
                      {admin.email === currentAdminEmail && (
                        <span style={{ marginLeft: '6px', fontSize: '11px', color: '#1B4A8C', background: '#EBF1FA', padding: '1px 6px', borderRadius: '3px' }}>
                          나
                        </span>
                      )}
                    </td>
                    <td style={s.td}>
                      {admin.addedAt?.toDate?.().toLocaleDateString('ko-KR') ?? '-'}
                    </td>
                    <td style={s.td}>{admin.addedBy ?? '-'}</td>
                    <td style={s.td}>
                      {admin.email !== currentAdminEmail && (
                        <button
                          style={{ ...s.btnMini, background: '#FEF2F2', color: '#B91C1C' }}
                          onClick={() => handleRemove(admin.email)}
                        >
                          삭제
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════
// 설정 탭
// ══════════════════════════════════════════════
function SettingsTab() {
  const [days,    setDays]    = useState(90);
  const [loading, setLoading] = useState(true);
  const [msg,     setMsg]     = useState(null);

  useEffect(() => {
    getDoc(doc(db, 'settings', 'config')).then(snap => {
      if (snap.exists()) setDays(snap.data().enrollmentDays ?? 90);
      setLoading(false);
    });
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setMsg(null);
    try {
      await setDoc(doc(db, 'settings', 'config'), { enrollmentDays: Number(days) }, { merge: true });
      setMsg({ type: 'ok', text: '저장되었습니다. 이후 신규 등록 수강생부터 적용됩니다.' });
    } catch {
      setMsg({ type: 'err', text: '저장 중 오류가 발생했습니다.' });
    }
  };

  if (loading) return <div style={s.loadingText}>불러오는 중...</div>;

  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>수강 기간 기본값</div>
      <div style={{ fontSize: '13px', color: '#5C5C72', marginBottom: '16px', lineHeight: '1.6' }}>
        신규 수강생 등록 시 기본으로 적용되는 수강 가능 기간입니다.<br />
        기존 수강생의 만료일에는 영향이 없습니다.
      </div>
      <form onSubmit={handleSave} style={s.enrollForm}>
        <input
          type="number" min="1" max="365" required
          value={days}
          onChange={e => setDays(e.target.value)}
          style={{ ...s.input, width: '100px' }}
        />
        <span style={{ fontSize: '13px', color: '#5C5C72' }}>일</span>
        <button type="submit" style={s.btnPrimary}>저장</button>
      </form>
      {msg && (
        <div style={{
          ...s.msgBox,
          background: msg.type === 'ok' ? '#EBF5EF' : '#FEF2F2',
          color:      msg.type === 'ok' ? '#2C6E49' : '#B91C1C',
        }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

// ── 공통 서브 컴포넌트 ────────────────────────

function SummaryCard({ label, value, color = '#1A1A2E' }) {
  return (
    <div style={s.summaryCard}>
      <div style={{ ...s.summaryValue, color }}>{value}</div>
      <div style={s.summaryLabel}>{label}</div>
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div
      style={s.modalOverlay}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={s.modalBox}>{children}</div>
    </div>
  );
}

// ── 스타일 ────────────────────────────────────
const s = {
  page: { minHeight: '100vh', background: '#F7F6F4', fontFamily: 'inherit' },
  header: {
    background: '#FFFFFF', borderBottom: '0.5px solid #E2E0DB',
    padding: '14px 32px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
  },
  headerLeft:  { display: 'flex', flexDirection: 'column', gap: '2px' },
  headerTitle: { fontSize: '16px', fontWeight: '500', color: '#1A1A2E' },
  headerSub:   { fontSize: '12px', color: '#9898AA' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  adminBadge:  { fontSize: '11px', fontWeight: '500', background: '#EBF1FA', color: '#1B4A8C', padding: '2px 8px', borderRadius: '4px' },
  adminEmail:  { fontSize: '13px', color: '#5C5C72' },
  signOutBtn:  { fontSize: '13px', color: '#5C5C72', background: 'none', border: '0.5px solid #E2E0DB', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit' },
  tabBar:      { background: '#FFFFFF', borderBottom: '0.5px solid #E2E0DB', padding: '0 32px', display: 'flex', gap: '0' },
  tab:         { padding: '12px 20px', fontSize: '14px', fontWeight: '500', color: '#5C5C72', background: 'none', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontFamily: 'inherit', transition: 'color 0.15s' },
  tabActive:   { color: '#1B4A8C', borderBottomColor: '#1B4A8C' },
  content:     { maxWidth: '1000px', margin: '0 auto', padding: '28px 32px' },
  summaryRow:  { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '28px' },
  summaryCard: { background: '#FFFFFF', border: '0.5px solid #E2E0DB', borderRadius: '10px', padding: '16px 20px' },
  summaryValue:{ fontSize: '24px', fontWeight: '500', marginBottom: '4px' },
  summaryLabel:{ fontSize: '12px', color: '#9898AA' },
  section:     { background: '#FFFFFF', border: '0.5px solid #E2E0DB', borderRadius: '10px', padding: '20px 24px', marginBottom: '16px' },
  sectionTitle:{ fontSize: '15px', fontWeight: '500', color: '#1A1A2E', marginBottom: '14px' },
  enrollForm:  { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' },
  input:       { flex: '1', minWidth: '200px', padding: '9px 12px', border: '0.5px solid #E2E0DB', borderRadius: '6px', fontSize: '14px', color: '#1A1A2E', background: '#FFFFFF', fontFamily: 'inherit', outline: 'none' },
  btnPrimary:  { padding: '9px 20px', background: '#1B4A8C', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', color: '#FFFFFF', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  btnSecondary:{ padding: '9px 20px', background: '#FFFFFF', border: '0.5px solid #E2E0DB', borderRadius: '6px', fontSize: '14px', fontWeight: '500', color: '#1A1A2E', cursor: 'pointer', fontFamily: 'inherit' },
  btnMini:     { padding: '4px 10px', background: '#F7F6F4', border: '0.5px solid #E2E0DB', borderRadius: '4px', fontSize: '12px', color: '#5C5C72', cursor: 'pointer', fontFamily: 'inherit' },
  msgBox:      { marginTop: '10px', padding: '8px 12px', borderRadius: '6px', fontSize: '13px' },
  tableWrap:   { overflowX: 'auto' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:          { padding: '8px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#9898AA', borderBottom: '0.5px solid #E2E0DB', whiteSpace: 'nowrap' },
  td:          { padding: '10px 12px', borderBottom: '0.5px solid #E2E0DB', color: '#1A1A2E', verticalAlign: 'middle' },
  statusBadge: { fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '4px' },
  actionRow:   { display: 'flex', gap: '6px' },
  loadingText: { fontSize: '13px', color: '#9898AA', padding: '12px 0' },
  emptyText:   { fontSize: '13px', color: '#9898AA', padding: '12px 0' },
  modalOverlay:{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modalBox:    { background: '#FFFFFF', borderRadius: '12px', padding: '28px 32px', width: '100%', maxWidth: '400px' },
  modalTitle:  { fontSize: '16px', fontWeight: '500', color: '#1A1A2E', marginBottom: '10px' },
  modalDesc:   { fontSize: '13px', color: '#5C5C72', lineHeight: '1.6', marginBottom: '16px' },
  modalRow:    { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' },
  modalLabel:  { fontSize: '13px', color: '#1A1A2E', whiteSpace: 'nowrap' },
  modalBtns:   { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
};
